import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, BellOff, BookmarkPlus, Bookmark, Loader2, Play, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Chip, Panel } from "@/components/si/Bits";
import { api } from "@/lib/api";
import { SI, siErr, useSI } from "@/lib/sourcing";

// §99 Saved quick searches with daily/weekly "new matching candidates" emails.
const EP = `${SI}/saved-searches`;
const FREQ = [["daily", "Daily"], ["weekly", "Weekly"]];
const KEYS = ["company", "title", "keywords", "location", "person_name"];
const label = (p = {}) => KEYS.map((k) => p[k]).filter(Boolean).join(" · ") || "Search";
export const searchUrl = (p = {}) => {
  const q = new URLSearchParams(Object.entries({ ...Object.fromEntries(KEYS.map((k) => [k, p[k]])), sources: (p.sources || []).join(","), max_people: p.max_people }).filter(([, v]) => v));
  return `${SI}/quick?${q}`;
};

export const SaveSearchButton = ({ params, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [freq, setFreq] = useState("daily");
  const [busy, setBusy] = useState(false);
  const show = () => { setName(label(params).slice(0, 80)); setFreq("daily"); setOpen(true); };
  const save = async () => {
    setBusy(true);
    try {
      await api.post(EP, { name: name.trim(), params, frequency: freq, email_alerts: true });
      toast.success(`Saved. We'll email you ${freq} when new matching candidates appear.`); setOpen(false); onSaved?.();
    } catch (e) { toast.error(siErr(e)); } finally { setBusy(false); }
  };
  return <>
    <Button size="sm" variant="outline" onClick={show} className="h-8 border-[#c79bff]/50 text-[#c79bff]" data-testid="qs-save-search"><BookmarkPlus className="h-3.5 w-3.5" />Save search</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="border-[#27272A] bg-[#0f0f12]" data-testid="ss-dialog">
        <DialogTitle className="font-display text-zinc-50">Save this search</DialogTitle>
        <p className="text-xs text-zinc-400">We'll re-run it for you and email you only the new candidates that match.</p>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this search" className="h-9" data-testid="ss-name" />
        <div className="flex items-center gap-2"><span className="text-[10px] uppercase tracking-wider text-zinc-500">Email me</span>
          {FREQ.map(([k, l]) => <Chip key={k} active={freq === k} onClick={() => setFreq(k)} testid={`ss-freq-${k}`}>{l}</Chip>)}</div>
        <Button onClick={save} disabled={busy || !name.trim()} className="bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="ss-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save search</Button>
      </DialogContent>
    </Dialog>
  </>;
};

const Row = ({ s, reload }) => {
  const nav = useNavigate();
  const patch = async (b) => { try { await api.patch(`${EP}/${s.id}`, b); reload(); } catch (e) { toast.error(siErr(e)); } };
  const del = async () => { if (!window.confirm(`Delete "${s.name}"?`)) return; try { await api.delete(`${EP}/${s.id}`); toast.success("Saved search deleted"); reload(); } catch (e) { toast.error(siErr(e)); } };
  return (
    <div className="border-t border-[#1f1f23] py-2.5" data-testid={`ss-row-${s.id}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1"><p className="truncate text-sm text-zinc-100">{s.name}</p><p className="truncate text-[11px] text-zinc-500">{label(s.params)}</p></div>
        {s.new_count > 0 && <span className="shrink-0 rounded-full bg-[#10B981]/15 px-2 py-0.5 text-[10px] font-semibold text-[#34D399]" data-testid={`ss-new-${s.id}`}>{s.new_count} new</span>}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <button onClick={() => nav(searchUrl(s.params))} className="inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/40 px-2 py-0.5 text-[10px] text-[#F3C649] hover:bg-[#D4AF37]/10" data-testid={`ss-run-${s.id}`}><Play className="h-3 w-3" />Run now</button>
        <select value={s.frequency} onChange={(e) => patch({ frequency: e.target.value })} className="h-6 rounded-full border border-[#27272A] bg-[#121215] px-2 text-[10px] text-zinc-300" data-testid={`ss-freq-${s.id}`}>{FREQ.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <button onClick={() => patch({ email_alerts: !s.email_alerts })} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${s.email_alerts ? "border-[#10B981]/40 text-[#34D399]" : "border-[#27272A] text-zinc-500"}`} data-testid={`ss-alerts-${s.id}`}>{s.email_alerts ? <><Bell className="h-3 w-3" />Emails on</> : <><BellOff className="h-3 w-3" />Emails paused</>}</button>
        <button onClick={del} title="Delete" className="ml-auto text-zinc-500 hover:text-red-300" data-testid={`ss-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
};

export const SavedSearches = ({ q }) => {
  const own = useSI(q ? null : EP);
  const d = q || own;
  const items = d.data?.items || [];
  return (
    <Panel title={<span className="flex items-center gap-2"><Bookmark className="h-4 w-4 text-[#c79bff]" />Saved searches</span>} testid="ss-panel">
      {d.loading && !d.data ? <Loader2 className="h-4 w-4 animate-spin text-[#c79bff]" /> : d.missing || d.error ? <p className="text-xs text-zinc-500" data-testid="ss-unavailable">Saved searches aren't live on the backend yet.</p>
        : !items.length ? <p className="text-xs text-zinc-500" data-testid="ss-empty">Run a quick search and click "Save search" to get emailed when new matching candidates appear.</p>
        : items.map((s) => <Row key={s.id} s={s} reload={d.reload} />)}
    </Panel>
  );
};

export const useSavedSearches = () => useSI(EP);
